/**
 * Bausteine eines Mietverhältnisses: Anlegen, Mieterhöhung (§ 558/557a/557b),
 * Kommunikationsverlauf, Übergabeprotokoll, Zahlungen und Bankabgleich.
 * Eingebunden in der Einheitenakte (Objekt → Etage → Einheit) und in der Objektübersicht.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { indexmiete557b } from '@nkp/shared';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatDatum, formatEuro, parseEuroEingabe } from '../lib/format';

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

export interface EinheitV {
  id: string;
  bezeichnung: string;
  wohnflaecheQm: number | null;
}
export interface MieterV {
  id: string;
  name: string;
}
export type Mietart = 'normal' | 'staffel' | 'index';
export interface LeaseV {
  id: string;
  einheitId: string;
  einheit: string;
  mieter: string;
  beginn: string;
  ende: string | null;
  kaltmieteCent: number;
  vzNkCent: number;
  vzHeizCent: number;
  personen: number;
  mietart: Mietart;
  letzteErhoehungAm: string | null;
  kautionCent: number;
  kautionStatus: KautionStatus;
}

export const MIETART_LABEL: Record<Mietart, string> = {
  normal: 'Normalmiete',
  staffel: 'Staffelmiete',
  index: 'Indexmiete',
};

export type KautionStatus = 'offen' | 'erhalten' | 'angelegt' | 'teilw_einbehalten' | 'zurueckgezahlt';
export const KAUTION_LABEL: Record<KautionStatus, string> = {
  offen: 'Kaution offen',
  erhalten: 'Kaution erhalten',
  angelegt: 'Kaution angelegt',
  teilw_einbehalten: 'teilw. einbehalten',
  zurueckgezahlt: 'zurückgezahlt',
};
export function kautionTon(s: KautionStatus): 'warnung' | 'info' | 'erfolg' | 'neutral' {
  if (s === 'offen') return 'warnung';
  if (s === 'angelegt') return 'erfolg';
  if (s === 'zurueckgezahlt') return 'neutral';
  return 'info';
}

type Begrenzung = 'vergleichsmiete' | 'kappungsgrenze' | 'bereits_erreicht';

interface Ergebnis558 {
  aktuelleKaltmieteCent: number;
  vergleichsmieteGesamtCent: number;
  kappungsobergrenzeCent: number;
  neueKaltmieteCent: number;
  erhoehungCent: number;
  erhoehungProzent: number;
  begrenztDurch: Begrenzung;
  istQmCent: number;
  abweichungQmProzent: number;
  fruehestesVerlangenAm: string;
  wirksamAbFruehestensAm: string;
  sperrfristLaeuft: boolean;
}

interface HoehungAntwort {
  mietart: Mietart;
  erhoehung: Ergebnis558 | null;
  schreiben: string | null;
  hinweise: string[];
}

const BEGRENZUNG_LABEL: Record<Begrenzung, string> = {
  vergleichsmiete: 'Vergleichsmiete (Mietspiegel)',
  kappungsgrenze: 'Kappungsgrenze',
  bereits_erreicht: 'Vergleichsmiete bereits erreicht',
};

/** Kalendergenaues Monat-Addieren (Demo-Modus, ohne Server). */
function addMonate(iso: string, monate: number): string {
  const [j, m, t] = iso.split('-').map(Number);
  const ziel = m - 1 + monate;
  const zj = j + Math.floor(ziel / 12);
  const zm = ((ziel % 12) + 12) % 12;
  const letzter = new Date(Date.UTC(zj, zm + 1, 0)).getUTCDate();
  const zt = Math.min(t, letzter);
  return `${String(zj).padStart(4, '0')}-${String(zm + 1).padStart(2, '0')}-${String(zt).padStart(2, '0')}`;
}

/** § 558 im Demo-Modus lokal rechnen — identische Logik wie der Server-Rechenkern. */
function demo558(
  kaltmieteCent: number,
  flaecheQm: number,
  mietspiegelQmCent: number,
  kappung: 15 | 20,
  basis: string,
): Ergebnis558 {
  const heute = new Date().toISOString().slice(0, 10);
  const vergleich = Math.round(mietspiegelQmCent * flaecheQm);
  const kappungsgrenze = Math.round(kaltmieteCent * (1 + kappung / 100));
  const neue = Math.max(kaltmieteCent, Math.min(vergleich, kappungsgrenze));
  const erhoehung = neue - kaltmieteCent;
  const istQm = Math.round(kaltmieteCent / flaecheQm);
  const begrenzt: Begrenzung =
    vergleich <= kaltmieteCent
      ? 'bereits_erreicht'
      : kappungsgrenze < vergleich
        ? 'kappungsgrenze'
        : 'vergleichsmiete';
  const wirksam15 = addMonate(basis, 15);
  const wirksamZugang = `${addMonate(heute, 3).slice(0, 7)}-01`;
  return {
    aktuelleKaltmieteCent: kaltmieteCent,
    vergleichsmieteGesamtCent: vergleich,
    kappungsobergrenzeCent: kappungsgrenze,
    neueKaltmieteCent: neue,
    erhoehungCent: erhoehung,
    erhoehungProzent: kaltmieteCent === 0 ? 0 : Math.round((erhoehung / kaltmieteCent) * 1000) / 10,
    begrenztDurch: begrenzt,
    istQmCent: istQm,
    abweichungQmProzent:
      mietspiegelQmCent === 0 ? 0 : Math.round(((istQm - mietspiegelQmCent) / mietspiegelQmCent) * 1000) / 10,
    fruehestesVerlangenAm: addMonate(basis, 12),
    wirksamAbFruehestensAm: wirksam15 > wirksamZugang ? wirksam15 : wirksamZugang,
    sperrfristLaeuft: heute < addMonate(basis, 12),
  };
}

/* ------------------------------------------------------------------ Mietverhältnis */

export function LeaseFormular({
  einheiten,
  mieter,
  onAngelegt,
  onAbbruch,
  festeEinheitId,
  vorgabeBeginn,
  titel = 'Neues Mietverhältnis',
}: {
  einheiten: EinheitV[];
  mieter: MieterV[];
  onAngelegt: (l: LeaseV, neuerMieter?: MieterV) => void;
  onAbbruch: () => void;
  /** Einheit ist vorgegeben (Einheitenakte) — keine Auswahl. */
  festeEinheitId?: string;
  vorgabeBeginn?: string;
  titel?: string;
}) {
  const [einheitId, setEinheitId] = useState(festeEinheitId ?? einheiten[0]?.id ?? '');
  const [mieterModus, setMieterModus] = useState<'bestehend' | 'neu'>(mieter.length > 0 ? 'bestehend' : 'neu');
  const [mieterId, setMieterId] = useState(mieter[0]?.id ?? '');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [beginn, setBeginn] = useState(vorgabeBeginn ?? `${new Date().getFullYear()}-01-01`);
  const [ende, setEnde] = useState('');
  const [mietart, setMietart] = useState<Mietart>('normal');
  const [kaltmiete, setKaltmiete] = useState('');
  const [letzteErhoehung, setLetzteErhoehung] = useState('');
  const [vzNk, setVzNk] = useState('');
  const [vzHeiz, setVzHeiz] = useState('');
  const [personen, setPersonen] = useState('1');
  const [kaution, setKaution] = useState('');
  const [kautionStatus, setKautionStatus] = useState<KautionStatus>('offen');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!einheitId) {
      setFehler('Bitte eine Einheit wählen.');
      return;
    }
    if (mieterModus === 'neu' && !nachname.trim()) {
      setFehler('Bitte den Nachnamen des Mieters angeben.');
      return;
    }
    const nkCent = parseEuroEingabe(vzNk) ?? 0;
    const heizCent = parseEuroEingabe(vzHeiz) ?? 0;
    const kaltCent = parseEuroEingabe(kaltmiete) ?? 0;
    const anzahl = Math.max(1, Number(personen) || 1);
    const einheitBez = einheiten.find((x) => x.id === einheitId)?.bezeichnung ?? 'Einheit';

    setSendet(true);
    try {
      let mId = mieterId;
      let neuerMieter: MieterV | undefined;
      const name = [vorname.trim(), nachname.trim()].filter(Boolean).join(' ');

      if (!DEMO) {
        if (mieterModus === 'neu') {
          const m = await api.post<{ mieter: { id: string; name: string } }>('/mieter', {
            vorname: vorname.trim() || undefined,
            nachname: nachname.trim(),
          });
          mId = m.mieter.id;
          neuerMieter = { id: m.mieter.id, name: m.mieter.name };
        }
        await api.post('/mietverhaeltnisse', {
          einheitId,
          mieterId: mId,
          beginn,
          ende: ende || undefined,
          kaltmieteCent: kaltCent,
          vzNebenkostenCent: nkCent,
          vzHeizkostenCent: heizCent,
          personenzahl: anzahl,
          mietart,
          letzteErhoehungAm: letzteErhoehung || undefined,
          kautionCent: parseEuroEingabe(kaution) ?? 0,
          kautionStatus,
        });
      } else if (mieterModus === 'neu') {
        neuerMieter = { id: `m-${Date.now()}`, name };
        mId = neuerMieter.id;
      }

      const mieterName = mieterModus === 'neu' ? name : mieter.find((x) => x.id === mId)?.name ?? 'Mieter';
      onAngelegt(
        {
          id: `neu-${Date.now()}`,
          einheitId,
          einheit: einheitBez,
          mieter: mieterName,
          beginn,
          ende: ende || null,
          kaltmieteCent: kaltCent,
          vzNkCent: nkCent,
          vzHeizCent: heizCent,
          personen: anzahl,
          mietart,
          letzteErhoehungAm: letzteErhoehung || null,
          kautionCent: parseEuroEingabe(kaution) ?? 0,
          kautionStatus,
        },
        neuerMieter,
      );
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Das Mietverhältnis konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel={titel} className="mb-3">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {!festeEinheitId && (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-fg">Einheit</span>
              <select className={feldKlasse} value={einheitId} onChange={(e) => setEinheitId(e.target.value)}>
                {einheiten.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.bezeichnung}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Mieter</span>
            <select
              className={feldKlasse}
              value={mieterModus === 'neu' ? '__neu' : mieterId}
              onChange={(e) => {
                if (e.target.value === '__neu') setMieterModus('neu');
                else {
                  setMieterModus('bestehend');
                  setMieterId(e.target.value);
                }
              }}
            >
              {mieter.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
              <option value="__neu">+ Neuen Mieter anlegen</option>
            </select>
          </label>
        </div>

        {mieterModus === 'neu' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Vorname (optional)" value={vorname} onChange={(e) => setVorname(e.target.value)} />
            <Input label="Nachname" value={nachname} onChange={(e) => setNachname(e.target.value)} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Mietbeginn" type="date" value={beginn} onChange={(e) => setBeginn(e.target.value)} />
          <Input label="Mietende (leer = laufend)" type="date" value={ende} onChange={(e) => setEnde(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Mietart</span>
            <select className={feldKlasse} value={mietart} onChange={(e) => setMietart(e.target.value as Mietart)}>
              <option value="normal">Normalmiete</option>
              <option value="staffel">Staffelmiete (§ 557a)</option>
              <option value="index">Indexmiete (§ 557b)</option>
            </select>
          </label>
          <Input label="Kaltmiete/Monat (€)" inputMode="decimal" value={kaltmiete} onChange={(e) => setKaltmiete(e.target.value)} />
          <Input label="Letzte Mieterhöhung" type="date" value={letzteErhoehung} onChange={(e) => setLetzteErhoehung(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="VZ Nebenkosten/Monat (€)" inputMode="decimal" value={vzNk} onChange={(e) => setVzNk(e.target.value)} />
          <Input label="VZ Heizkosten/Monat (€)" inputMode="decimal" value={vzHeiz} onChange={(e) => setVzHeiz(e.target.value)} />
          <Input label="Personen" inputMode="numeric" value={personen} onChange={(e) => setPersonen(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Kaution (€)" inputMode="decimal" value={kaution} onChange={(e) => setKaution(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Kautionsstatus</span>
            <select className={feldKlasse} value={kautionStatus} onChange={(e) => setKautionStatus(e.target.value as KautionStatus)}>
              {(Object.keys(KAUTION_LABEL) as KautionStatus[]).map((k) => (
                <option key={k} value={k}>{KAUTION_LABEL[k]}</option>
              ))}
            </select>
          </label>
        </div>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Mietverhältnis speichern'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ Mieterhöhung */

function Zeile({ label, wert, betont }: { label: string; wert: string; betont?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-sm text-fg-muted">{label}</span>
      <span className={`tabellenziffern text-sm ${betont ? 'font-semibold text-fg' : 'text-fg'}`}>{wert}</span>
    </div>
  );
}

export function MieterhoehungPanel({
  lease,
  flaecheQm,
  mietspiegelCent,
}: {
  lease: LeaseV;
  flaecheQm: number | null;
  mietspiegelCent: number | null;
}) {
  const [antwort, setAntwort] = useState<HoehungAntwort | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);

  useEffect(() => {
    let aktiv = true;
    setLaden(true);
    setFehler(null);

    if (DEMO) {
      const basis = lease.letzteErhoehungAm ?? lease.beginn;
      let ergebnis: HoehungAntwort;
      if (lease.mietart === 'staffel') {
        ergebnis = {
          mietart: 'staffel',
          erhoehung: null,
          schreiben: null,
          hinweise: [
            'Bei Staffelmiete (§ 557a BGB) ist die Erhöhung im Mietvertrag festgelegt. Eine Erhöhung nach Vergleichsmiete oder Kappungsgrenze ist ausgeschlossen.',
          ],
        };
      } else if (lease.mietart === 'index') {
        ergebnis = {
          mietart: 'index',
          erhoehung: null,
          schreiben: null,
          hinweise: [
            'Bei Indexmiete (§ 557b BGB) folgt die Miete dem Verbraucherpreisindex. Basisindex und aktueller Index werden zur Berechnung benötigt.',
          ],
        };
      } else if (!flaecheQm || !mietspiegelCent) {
        ergebnis = {
          mietart: 'normal',
          erhoehung: null,
          schreiben: null,
          hinweise: ['Für dieses Objekt ist keine ortsübliche Vergleichsmiete (Mietspiegel) hinterlegt.'],
        };
      } else {
        const e = demo558(lease.kaltmieteCent, flaecheQm, mietspiegelCent, 20, basis);
        const hinweise: string[] = [];
        if (e.begrenztDurch === 'kappungsgrenze')
          hinweise.push('Die Erhöhung ist durch die Kappungsgrenze von 20 % begrenzt, nicht durch die Vergleichsmiete.');
        if (e.sperrfristLaeuft)
          hinweise.push(`Die Jahressperrfrist läuft noch. Ein Erhöhungsverlangen ist frühestens ab dem ${formatDatum(e.fruehestesVerlangenAm)} zulässig.`);
        ergebnis = { mietart: 'normal', erhoehung: e, schreiben: null, hinweise };
      }
      setAntwort(ergebnis);
      setLaden(false);
      return;
    }

    api
      .get<HoehungAntwort>(`/mieterhoehung/${lease.id}`)
      .then((d) => {
        if (aktiv) setAntwort(d);
      })
      .catch((e) => {
        if (aktiv) setFehler(e instanceof ApiFehler ? e.message : 'Der Vorschlag konnte nicht geladen werden.');
      })
      .finally(() => {
        if (aktiv) setLaden(false);
      });
    return () => {
      aktiv = false;
    };
  }, [lease.id, lease.mietart, lease.kaltmieteCent, lease.beginn, lease.letzteErhoehungAm, flaecheQm, mietspiegelCent]);

  async function kopieren(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch {
      /* Zwischenablage nicht verfügbar — Nutzer kann den Text markieren. */
    }
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      {laden ? (
        <p className="text-sm text-fg-muted">Wird berechnet …</p>
      ) : fehler ? (
        <p className="text-sm text-danger">{fehler}</p>
      ) : !antwort ? null : antwort.erhoehung ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <h3 className="mb-1 text-sm font-semibold text-fg">Zulässige Erhöhung (§ 558 BGB)</h3>
            <Zeile label="Aktuelle Kaltmiete" wert={formatEuro(antwort.erhoehung.aktuelleKaltmieteCent)} />
            <Zeile label="Vergleichsmiete (Mietspiegel)" wert={formatEuro(antwort.erhoehung.vergleichsmieteGesamtCent)} />
            <Zeile label="Kappungsobergrenze" wert={formatEuro(antwort.erhoehung.kappungsobergrenzeCent)} />
            <div className="my-1 border-t border-border" />
            <Zeile label="Neue Kaltmiete" wert={formatEuro(antwort.erhoehung.neueKaltmieteCent)} betont />
            <Zeile
              label="Erhöhung"
              wert={`${formatEuro(antwort.erhoehung.erhoehungCent)} (${antwort.erhoehung.erhoehungProzent.toLocaleString('de-DE')} %)`}
              betont
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge ton="info">Begrenzt durch: {BEGRENZUNG_LABEL[antwort.erhoehung.begrenztDurch]}</Badge>
              {antwort.erhoehung.sperrfristLaeuft ? (
                <Badge ton="warnung">Sperrfrist läuft</Badge>
              ) : (
                <Badge ton="erfolg">Verlangen zulässig</Badge>
              )}
            </div>
            <p className="mt-2 text-xs text-fg-subtle">
              Frühestes Verlangen: {formatDatum(antwort.erhoehung.fruehestesVerlangenAm)} · Wirksam frühestens ab:{' '}
              {formatDatum(antwort.erhoehung.wirksamAbFruehestensAm)}
            </p>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-fg">Erhöhungsschreiben</h3>
              {antwort.schreiben && (
                <Button variante="dezent" groesse="sm" onClick={() => kopieren(antwort.schreiben!)}>
                  {kopiert ? 'Kopiert' : 'Kopieren'}
                </Button>
              )}
            </div>
            {antwort.schreiben ? (
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-surface-2 p-3 text-xs leading-relaxed text-fg">
                {antwort.schreiben}
              </pre>
            ) : (
              <p className="text-sm text-fg-muted">
                {DEMO
                  ? 'Das vorformulierte Schreiben erscheint im Live-Betrieb.'
                  : 'Keine Erhöhung möglich — kein Schreiben nötig.'}
              </p>
            )}
          </div>
          {antwort.hinweise.length > 0 && (
            <ul className="md:col-span-2 space-y-1">
              {antwort.hinweise.map((h, i) => (
                <li key={i} className="text-xs text-fg-muted">
                  · {h}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : antwort.mietart === 'index' ? (
        <IndexRechner lease={lease} hinweise={antwort.hinweise} />
      ) : (
        <div className="space-y-1">
          {antwort.hinweise.map((h, i) => (
            <p key={i} className="text-sm text-fg-muted">
              {h}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** Indexmiete (§ 557b): interaktive Berechnung aus Basis- und aktuellem Verbraucherpreisindex. */
function IndexRechner({ lease, hinweise }: { lease: LeaseV; hinweise: string[] }) {
  const [basis, setBasis] = useState('');
  const [aktuell, setAktuell] = useState('');

  const basisZahl = Number(basis.replace(',', '.'));
  const aktuellZahl = Number(aktuell.replace(',', '.'));
  const gueltig = basisZahl > 0 && aktuellZahl > 0;

  const ergebnis = gueltig
    ? indexmiete557b({
        aktuelleKaltmieteCent: lease.kaltmieteCent,
        basisIndex: basisZahl,
        aktuellerIndex: aktuellZahl,
        letzteAenderungAm: lease.letzteErhoehungAm ?? lease.beginn,
        stichtag: new Date().toISOString().slice(0, 10),
      })
    : null;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-fg">Indexmiete (§ 557b BGB)</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Basisindex (VPI)" inputMode="decimal" placeholder="z. B. 100,0" value={basis} onChange={(e) => setBasis(e.target.value)} />
          <Input label="Aktueller Index" inputMode="decimal" placeholder="z. B. 110,4" value={aktuell} onChange={(e) => setAktuell(e.target.value)} />
        </div>
        <p className="mt-2 text-xs text-fg-subtle">
          Verbraucherpreisindex Deutschland (Statistisches Bundesamt). Basisindex = Stand bei
          der letzten Festsetzung.
        </p>
      </div>
      <div>
        {ergebnis ? (
          <>
            <Zeile label="Aktuelle Kaltmiete" wert={formatEuro(lease.kaltmieteCent)} />
            <Zeile label="Neue Kaltmiete" wert={formatEuro(ergebnis.neueKaltmieteCent)} betont />
            <Zeile
              label="Erhöhung"
              wert={`${formatEuro(ergebnis.erhoehungCent)} (${ergebnis.erhoehungProzent.toLocaleString('de-DE')} %)`}
              betont
            />
            <div className="mt-2">
              {ergebnis.sperrfristLaeuft ? (
                <Badge ton="warnung">Jahresfrist läuft bis {formatDatum(ergebnis.fruehestensAm)}</Badge>
              ) : (
                <Badge ton="erfolg">Anpassung ab {formatDatum(ergebnis.fruehestensAm)} zulässig</Badge>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-fg-muted">Basis- und aktuellen Index eingeben, um die zulässige Miete zu berechnen.</p>
        )}
        {hinweise.length > 0 && (
          <ul className="mt-2 space-y-1">
            {hinweise.map((h, i) => (
              <li key={i} className="text-xs text-fg-subtle">· {h}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Kommunikation */

type KommTyp = 'brief' | 'email' | 'telefon' | 'notiz' | 'sonstiges';
type KommRichtung = 'eingehend' | 'ausgehend' | 'intern';

interface KommEintrag {
  id: string;
  typ: KommTyp;
  richtung: KommRichtung;
  datum: string;
  betreff: string;
  inhalt: string | null;
}

const KOMM_TYP_LABEL: Record<KommTyp, string> = {
  brief: 'Brief',
  email: 'E-Mail',
  telefon: 'Telefon',
  notiz: 'Notiz',
  sonstiges: 'Sonstiges',
};
const KOMM_RICHTUNG_LABEL: Record<KommRichtung, string> = {
  eingehend: 'eingehend',
  ausgehend: 'ausgehend',
  intern: 'intern',
};

export function KommunikationPanel({ lease }: { lease: LeaseV }) {
  const [eintraege, setEintraege] = useState<KommEintrag[]>([]);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);
  const [typ, setTyp] = useState<KommTyp>('notiz');
  const [richtung, setRichtung] = useState<KommRichtung>('ausgehend');
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [betreff, setBetreff] = useState('');
  const [inhalt, setInhalt] = useState('');
  const [sendet, setSendet] = useState(false);

  useEffect(() => {
    if (DEMO) {
      setEintraege([
        {
          id: 'k-1',
          typ: 'brief',
          richtung: 'ausgehend',
          datum: '2026-09-01',
          betreff: 'Mieterhöhungsverlangen § 558',
          inhalt: 'Zustellung per Einwurf-Einschreiben.',
        },
        {
          id: 'k-2',
          typ: 'telefon',
          richtung: 'eingehend',
          datum: '2026-08-14',
          betreff: 'Rückfrage zur Nebenkostenabrechnung',
          inhalt: null,
        },
      ]);
      return;
    }
    setLaden(true);
    api
      .get<{ eintraege: KommEintrag[] }>(`/kommunikation?mietverhaeltnisId=${lease.id}`)
      .then((d) => setEintraege(d.eintraege))
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Der Verlauf konnte nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, [lease.id]);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!betreff.trim()) return;
    setSendet(true);
    const neu: KommEintrag = {
      id: `neu-${Date.now()}`,
      typ,
      richtung,
      datum,
      betreff: betreff.trim(),
      inhalt: inhalt.trim() || null,
    };
    try {
      if (!DEMO) {
        const r = await api.post<{ eintrag: KommEintrag }>('/kommunikation', {
          mietverhaeltnisId: lease.id,
          typ,
          richtung,
          datum,
          betreff: betreff.trim(),
          inhalt: inhalt.trim() || undefined,
        });
        setEintraege((xs) => [r.eintrag, ...xs]);
      } else {
        setEintraege((xs) => [neu, ...xs]);
      }
      setBetreff('');
      setInhalt('');
      setFormOffen(false);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Der Eintrag konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg">Kommunikationsverlauf</h3>
        <Button variante="dezent" groesse="sm" onClick={() => setFormOffen((o) => !o)}>
          {formOffen ? 'Abbrechen' : 'Eintrag hinzufügen'}
        </Button>
      </div>

      {formOffen && (
        <form onSubmit={speichern} className="mb-3 space-y-3 rounded-lg border border-border bg-surface-2 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-fg">Art</span>
              <select className={feldKlasse} value={typ} onChange={(e) => setTyp(e.target.value as KommTyp)}>
                {(Object.keys(KOMM_TYP_LABEL) as KommTyp[]).map((t) => (
                  <option key={t} value={t}>{KOMM_TYP_LABEL[t]}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-fg">Richtung</span>
              <select className={feldKlasse} value={richtung} onChange={(e) => setRichtung(e.target.value as KommRichtung)}>
                {(Object.keys(KOMM_RICHTUNG_LABEL) as KommRichtung[]).map((rr) => (
                  <option key={rr} value={rr}>{KOMM_RICHTUNG_LABEL[rr]}</option>
                ))}
              </select>
            </label>
            <Input label="Datum" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </div>
          <Input label="Betreff" placeholder="z. B. Mängelanzeige Heizung" value={betreff} onChange={(e) => setBetreff(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Inhalt (optional)</span>
            <textarea className={`${feldKlasse} min-h-[70px]`} value={inhalt} onChange={(e) => setInhalt(e.target.value)} />
          </label>
          <Button type="submit" groesse="sm" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Eintrag speichern'}
          </Button>
        </form>
      )}

      {fehler && <p className="mb-2 text-sm text-danger">{fehler}</p>}
      {laden ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : eintraege.length === 0 ? (
        <p className="text-sm text-fg-muted">Noch kein dokumentierter Schriftverkehr.</p>
      ) : (
        <ol className="space-y-2">
          {eintraege.map((k) => (
            <li key={k.id} className="rounded-lg border border-border bg-surface px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge ton="neutral">{KOMM_TYP_LABEL[k.typ]}</Badge>
                <Badge ton={k.richtung === 'eingehend' ? 'info' : k.richtung === 'ausgehend' ? 'erfolg' : 'neutral'}>
                  {KOMM_RICHTUNG_LABEL[k.richtung]}
                </Badge>
                <span className="text-xs text-fg-subtle">{formatDatum(k.datum)}</span>
              </div>
              <p className="mt-1 text-sm font-medium text-fg">{k.betreff}</p>
              {k.inhalt && <p className="mt-0.5 text-sm text-fg-muted">{k.inhalt}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Übergabe */

type UebergabeArt = 'einzug' | 'auszug' | 'zwischen';
interface Schluessel {
  bezeichnung: string;
  anzahl: number;
}
interface Uebergabe {
  id: string;
  art: UebergabeArt;
  datum: string;
  zaehlerstaende: string | null;
  schluessel: Schluessel[];
  maengel: string | null;
  anwesend: string | null;
}

const UEBERGABE_LABEL: Record<UebergabeArt, string> = {
  einzug: 'Einzug',
  auszug: 'Auszug',
  zwischen: 'Zwischenübergabe',
};

export function UebergabePanel({ lease }: { lease: LeaseV }) {
  const [liste, setListe] = useState<Uebergabe[]>([]);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);

  const [art, setArt] = useState<UebergabeArt>('einzug');
  const [datum, setDatum] = useState(lease.beginn);
  const [zaehler, setZaehler] = useState('');
  const [schluessel, setSchluessel] = useState<Schluessel[]>([{ bezeichnung: 'Haustür', anzahl: 1 }]);
  const [maengel, setMaengel] = useState('');
  const [anwesend, setAnwesend] = useState('');
  const [sendet, setSendet] = useState(false);

  useEffect(() => {
    if (DEMO) {
      setListe([
        {
          id: 'u-1',
          art: 'einzug',
          datum: lease.beginn,
          zaehlerstaende: 'Strom 14.820 kWh · Kaltwasser 412 m³',
          schluessel: [
            { bezeichnung: 'Haustür', anzahl: 2 },
            { bezeichnung: 'Wohnung', anzahl: 3 },
          ],
          maengel: 'Kleiner Kratzer am Parkett im Flur',
          anwesend: 'Vermieter, Mieter',
        },
      ]);
      return;
    }
    setLaden(true);
    api
      .get<{ uebergaben: Uebergabe[] }>(`/uebergaben?mietverhaeltnisId=${lease.id}`)
      .then((d) => setListe(d.uebergaben))
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Übergaben konnten nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, [lease.id]);

  function setzeSchluessel(i: number, patch: Partial<Schluessel>) {
    setSchluessel((xs) => xs.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setSendet(true);
    const gefiltert = schluessel.filter((s) => s.bezeichnung.trim());
    const neu: Uebergabe = {
      id: `neu-${Date.now()}`,
      art,
      datum,
      zaehlerstaende: zaehler.trim() || null,
      schluessel: gefiltert,
      maengel: maengel.trim() || null,
      anwesend: anwesend.trim() || null,
    };
    try {
      if (!DEMO) {
        const r = await api.post<{ uebergabe: Uebergabe }>('/uebergaben', {
          mietverhaeltnisId: lease.id,
          einheitId: lease.einheitId || undefined,
          art,
          datum,
          zaehlerstaende: zaehler.trim() || undefined,
          schluessel: gefiltert,
          maengel: maengel.trim() || undefined,
          anwesend: anwesend.trim() || undefined,
        });
        setListe((xs) => [r.uebergabe, ...xs]);
      } else {
        setListe((xs) => [neu, ...xs]);
      }
      setFormOffen(false);
      setZaehler('');
      setMaengel('');
      setAnwesend('');
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Das Protokoll konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg">Übergabeprotokolle</h3>
        <Button variante="dezent" groesse="sm" onClick={() => setFormOffen((o) => !o)}>
          {formOffen ? 'Abbrechen' : 'Protokoll erstellen'}
        </Button>
      </div>

      {formOffen && (
        <form onSubmit={speichern} className="mb-3 space-y-3 rounded-lg border border-border bg-surface-2 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-fg">Art</span>
              <select className={feldKlasse} value={art} onChange={(e) => setArt(e.target.value as UebergabeArt)}>
                {(Object.keys(UEBERGABE_LABEL) as UebergabeArt[]).map((a) => (
                  <option key={a} value={a}>{UEBERGABE_LABEL[a]}</option>
                ))}
              </select>
            </label>
            <Input label="Datum" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </div>
          <Input label="Zählerstände" placeholder="z. B. Strom 14820 kWh, Kaltwasser 412 m³" value={zaehler} onChange={(e) => setZaehler(e.target.value)} />

          <div>
            <span className="mb-1.5 block text-sm font-medium text-fg">Schlüssel</span>
            <div className="space-y-2">
              {schluessel.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input className={`${feldKlasse} flex-1`} placeholder="Bezeichnung" value={s.bezeichnung} onChange={(e) => setzeSchluessel(i, { bezeichnung: e.target.value })} />
                  <input className={`${feldKlasse} w-20`} inputMode="numeric" value={String(s.anzahl)} onChange={(e) => setzeSchluessel(i, { anzahl: Math.max(0, Number(e.target.value) || 0) })} />
                  <Button type="button" variante="dezent" groesse="sm" onClick={() => setSchluessel((xs) => xs.filter((_, j) => j !== i))}>
                    ✕
                  </Button>
                </div>
              ))}
              <Button type="button" variante="dezent" groesse="sm" onClick={() => setSchluessel((xs) => [...xs, { bezeichnung: '', anzahl: 1 }])}>
                + Schlüssel
              </Button>
            </div>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Mängel / Zustand</span>
            <textarea className={`${feldKlasse} min-h-[60px]`} value={maengel} onChange={(e) => setMaengel(e.target.value)} />
          </label>
          <Input label="Anwesende" placeholder="z. B. Vermieter, Mieter" value={anwesend} onChange={(e) => setAnwesend(e.target.value)} />
          <Button type="submit" groesse="sm" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Protokoll speichern'}
          </Button>
        </form>
      )}

      {fehler && <p className="mb-2 text-sm text-danger">{fehler}</p>}
      {laden ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : liste.length === 0 ? (
        <p className="text-sm text-fg-muted">Noch kein Übergabeprotokoll.</p>
      ) : (
        <div className="space-y-2">
          {liste.map((u) => (
            <div key={u.id} className="rounded-lg border border-border bg-surface px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge ton={u.art === 'einzug' ? 'erfolg' : u.art === 'auszug' ? 'warnung' : 'neutral'}>
                  {UEBERGABE_LABEL[u.art]}
                </Badge>
                <span className="text-xs text-fg-subtle">{formatDatum(u.datum)}</span>
                {u.schluessel.length > 0 && (
                  <span className="text-xs text-fg-muted">
                    Schlüssel: {u.schluessel.map((s) => `${s.anzahl}× ${s.bezeichnung}`).join(', ')}
                  </span>
                )}
              </div>
              {u.zaehlerstaende && <p className="mt-1 text-sm text-fg-muted">Zähler: {u.zaehlerstaende}</p>}
              {u.maengel && <p className="mt-0.5 text-sm text-fg-muted">Mängel: {u.maengel}</p>}
              {u.anwesend && <p className="mt-0.5 text-xs text-fg-subtle">Anwesend: {u.anwesend}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Zahlungen */

type ZahlungArt = 'miete' | 'nachzahlung' | 'guthaben' | 'kaution' | 'sonstige';
interface Zahlung {
  id: string;
  datum: string;
  betragCent: number;
  art: ZahlungArt;
  verwendungszweck: string | null;
}
interface Forderung {
  jahr: number;
  warmmieteCent: number;
  faelligeMonate: number;
  sollCent: number;
  istCent: number;
  offenCent: number;
}

const ZAHLUNG_ART_LABEL: Record<ZahlungArt, string> = {
  miete: 'Miete',
  nachzahlung: 'Nachzahlung',
  guthaben: 'Guthaben',
  kaution: 'Kaution',
  sonstige: 'Sonstige',
};

export function ZahlungenPanel({ lease }: { lease: LeaseV }) {
  const [liste, setListe] = useState<Zahlung[]>([]);
  const [forderung, setForderung] = useState<Forderung | null>(null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [betrag, setBetrag] = useState('');
  const [art, setArt] = useState<ZahlungArt>('miete');
  const [sendet, setSendet] = useState(false);
  const [mahnStufe, setMahnStufe] = useState('1');
  const [mahnGebuehr, setMahnGebuehr] = useState('');
  const [mahnSchreiben, setMahnSchreiben] = useState<string | null>(null);
  const [mahnt, setMahnt] = useState(false);

  const warm = lease.kaltmieteCent + lease.vzNkCent + lease.vzHeizCent;

  function neuBerechnen(zahlungen: Zahlung[]) {
    const jahr = new Date().getUTCFullYear();
    const ist = zahlungen
      .filter((z) => (z.art === 'miete' || z.art === 'nachzahlung') && z.datum.startsWith(String(jahr)))
      .reduce((s, z) => s + z.betragCent, 0);
    const monat = new Date().getUTCMonth() + 1;
    const soll = warm * monat;
    setForderung({ jahr, warmmieteCent: warm, faelligeMonate: monat, sollCent: soll, istCent: ist, offenCent: soll - ist });
  }

  useEffect(() => {
    if (DEMO) {
      const jahr = new Date().getUTCFullYear();
      const demo: Zahlung[] = [
        { id: 'z-1', datum: `${jahr}-01-03`, betragCent: warm, art: 'miete', verwendungszweck: 'Miete Januar' },
        { id: 'z-2', datum: `${jahr}-02-02`, betragCent: warm, art: 'miete', verwendungszweck: 'Miete Februar' },
      ];
      setListe(demo);
      neuBerechnen(demo);
      return;
    }
    setLaden(true);
    Promise.all([
      api.get<{ zahlungen: Zahlung[] }>(`/zahlungen?mietverhaeltnisId=${lease.id}`),
      api.get<Forderung>(`/zahlungen/forderungen/${lease.id}`),
    ])
      .then(([z, f]) => {
        setListe(z.zahlungen);
        setForderung(f);
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Zahlungen konnten nicht geladen werden.'))
      .finally(() => setLaden(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lease.id]);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    const betragCent = parseEuroEingabe(betrag);
    if (betragCent === null) return;
    setSendet(true);
    const neu: Zahlung = { id: `neu-${Date.now()}`, datum, betragCent, art, verwendungszweck: null };
    try {
      if (!DEMO) {
        const r = await api.post<{ zahlung: Zahlung }>('/zahlungen', {
          mietverhaeltnisId: lease.id,
          datum,
          betragCent,
          art,
        });
        const neueListe = [r.zahlung, ...liste];
        setListe(neueListe);
        const f = await api.get<Forderung>(`/zahlungen/forderungen/${lease.id}`);
        setForderung(f);
      } else {
        const neueListe = [neu, ...liste];
        setListe(neueListe);
        neuBerechnen(neueListe);
      }
      setBetrag('');
      setFormOffen(false);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Zahlung konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  async function mahnen() {
    if (!forderung || forderung.offenCent <= 0) return;
    setMahnt(true);
    setMahnSchreiben(null);
    const gebuehr = parseEuroEingabe(mahnGebuehr) ?? 0;
    const stufe = Number(mahnStufe) || 1;
    try {
      if (!DEMO) {
        const r = await api.post<{ schreiben: string }>('/mahnungen', {
          mietverhaeltnisId: lease.id,
          stufe,
          betragOffenCent: forderung.offenCent,
          mahngebuehrCent: gebuehr,
        });
        setMahnSchreiben(r.schreiben);
      } else {
        const titel = stufe === 1 ? 'Zahlungserinnerung' : stufe === 2 ? '1. Mahnung' : '2. Mahnung — letzte Aufforderung';
        setMahnSchreiben(
          `${titel}\n\nSehr geehrte Damen und Herren,\n\noffene Forderung: ${formatEuro(forderung.offenCent)}` +
            (gebuehr > 0 ? `\nMahnkosten: ${formatEuro(gebuehr)}` : '') +
            `\n\nBitte begleichen Sie den Betrag fristgerecht.\n\nMit freundlichen Grüßen`,
        );
      }
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Mahnung konnte nicht erstellt werden.');
    } finally {
      setMahnt(false);
    }
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg">Zahlungen &amp; offene Forderungen</h3>
        <Button variante="dezent" groesse="sm" onClick={() => setFormOffen((o) => !o)}>
          {formOffen ? 'Abbrechen' : 'Zahlung erfassen'}
        </Button>
      </div>

      {forderung && (
        <div className="mb-3 grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-border bg-surface-2 p-3">
            <p className="text-xs text-fg-muted">Sollmiete {forderung.jahr}</p>
            <p className="mt-1 tabellenziffern text-sm font-semibold text-fg">{formatEuro(forderung.sollCent)}</p>
            <p className="text-xs text-fg-subtle">{forderung.faelligeMonate} × {formatEuro(forderung.warmmieteCent)}</p>
          </div>
          <div className="rounded-lg border border-border bg-surface-2 p-3">
            <p className="text-xs text-fg-muted">Eingegangen</p>
            <p className="mt-1 tabellenziffern text-sm font-semibold text-fg">{formatEuro(forderung.istCent)}</p>
          </div>
          <div className={`rounded-lg border p-3 ${forderung.offenCent > 0 ? 'border-warning bg-warning-soft' : 'border-border bg-surface-2'}`}>
            <p className="text-xs text-fg-muted">Offene Forderungen</p>
            <p className={`mt-1 tabellenziffern text-sm font-semibold ${forderung.offenCent > 0 ? 'text-warning-soft-fg' : 'text-success'}`}>
              {formatEuro(forderung.offenCent)}
            </p>
          </div>
        </div>
      )}

      {forderung && forderung.offenCent > 0 && (
        <div className="mb-3 rounded-lg border border-border bg-surface-2 p-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-fg">Mahnstufe</span>
              <select className={`${feldKlasse} w-44`} value={mahnStufe} onChange={(e) => setMahnStufe(e.target.value)}>
                <option value="1">Zahlungserinnerung</option>
                <option value="2">1. Mahnung</option>
                <option value="3">2. Mahnung</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-fg">Mahnkosten (€)</span>
              <input className={`${feldKlasse} w-24`} inputMode="decimal" placeholder="0,00" value={mahnGebuehr} onChange={(e) => setMahnGebuehr(e.target.value)} />
            </label>
            <Button variante="sekundaer" groesse="sm" onClick={mahnen} disabled={mahnt}>
              {mahnt ? '…' : 'Mahnung erstellen'}
            </Button>
          </div>
          {mahnSchreiben && (
            <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-surface p-3 text-xs leading-relaxed text-fg">
              {mahnSchreiben}
            </pre>
          )}
        </div>
      )}

      {formOffen && (
        <form onSubmit={speichern} className="mb-3 grid grid-cols-1 gap-3 rounded-lg border border-border bg-surface-2 p-3 sm:grid-cols-[10rem_1fr_10rem_auto] sm:items-end">
          <Input label="Datum" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
          <Input label="Betrag (€)" inputMode="decimal" value={betrag} onChange={(e) => setBetrag(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Art</span>
            <select className={feldKlasse} value={art} onChange={(e) => setArt(e.target.value as ZahlungArt)}>
              {(Object.keys(ZAHLUNG_ART_LABEL) as ZahlungArt[]).map((a) => (
                <option key={a} value={a}>{ZAHLUNG_ART_LABEL[a]}</option>
              ))}
            </select>
          </label>
          <Button type="submit" groesse="sm" disabled={sendet}>
            {sendet ? '…' : 'Erfassen'}
          </Button>
        </form>
      )}

      {fehler && <p className="mb-2 text-sm text-danger">{fehler}</p>}
      {laden ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : liste.length === 0 ? (
        <p className="text-sm text-fg-muted">Noch keine Zahlung erfasst.</p>
      ) : (
        <ol className="space-y-1.5">
          {liste.map((z) => (
            <li key={z.id} className="flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2">
              <div className="flex items-center gap-2">
                <Badge ton="neutral">{ZAHLUNG_ART_LABEL[z.art]}</Badge>
                <span className="text-xs text-fg-subtle">{formatDatum(z.datum)}</span>
                {z.verwendungszweck && <span className="text-xs text-fg-muted">{z.verwendungszweck}</span>}
              </div>
              <span className="tabellenziffern text-sm font-semibold text-fg">{formatEuro(z.betragCent)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Bankabgleich */

interface BankZeile {
  datum: string | null;
  betragCent: number | null;
  name: string;
  zweck: string;
  vorschlagMvId: string | null;
  vorschlagBezeichnung: string | null;
  mvId: string;
  uebernehmen: boolean;
}

export function BankabgleichPanel({ leases, onGebucht }: { leases: LeaseV[]; onGebucht: () => void }) {
  const [csv, setCsv] = useState('');
  const [zeilen, setZeilen] = useState<BankZeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laden, setLaden] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function vorschau() {
    setFehler(null);
    setMeldung(null);
    setLaden(true);
    try {
      let roh: Omit<BankZeile, 'mvId' | 'uebernehmen'>[];
      if (DEMO) {
        roh = [
          { datum: '2026-01-05', betragCent: 82000, name: 'Mustermann', zweck: 'Miete Januar', vorschlagMvId: leases[0]?.id ?? null, vorschlagBezeichnung: leases[0]?.mieter ?? null },
          { datum: '2026-01-06', betragCent: 79500, name: 'Unbekannt', zweck: 'Überweisung', vorschlagMvId: null, vorschlagBezeichnung: null },
        ];
      } else {
        const r = await api.post<{ zeilen: Omit<BankZeile, 'mvId' | 'uebernehmen'>[] }>('/bankabgleich/vorschau', { csv });
        roh = r.zeilen;
      }
      setZeilen(
        roh.map((z) => ({ ...z, mvId: z.vorschlagMvId ?? leases[0]?.id ?? '', uebernehmen: !!z.vorschlagMvId })),
      );
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Der Auszug konnte nicht gelesen werden.');
    } finally {
      setLaden(false);
    }
  }

  async function buchen() {
    const auswahl = (zeilen ?? []).filter((z) => z.uebernehmen && z.mvId && z.betragCent && z.datum);
    if (auswahl.length === 0) return;
    setLaden(true);
    try {
      if (!DEMO) {
        const r = await api.post<{ gebucht: number }>('/bankabgleich/buchen', {
          buchungen: auswahl.map((z) => ({
            mietverhaeltnisId: z.mvId,
            datum: z.datum,
            betragCent: z.betragCent,
            art: 'miete',
            verwendungszweck: z.zweck || undefined,
            bankRef: 'Bankabgleich',
          })),
        });
        setMeldung(`${r.gebucht} Zahlung(en) gebucht.`);
      } else {
        setMeldung(`${auswahl.length} Zahlung(en) gebucht (Vorschau).`);
      }
      setZeilen(null);
      setCsv('');
      onGebucht();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Buchung ist fehlgeschlagen.');
    } finally {
      setLaden(false);
    }
  }

  return (
    <Card titel="Bankabgleich" beschreibung="Kontoauszug als CSV oder CAMT.053 (XML) einfügen. Eingänge werden Mietverhältnissen zugeordnet und als Zahlungen gebucht." className="mb-4">
      {!zeilen ? (
        <div className="space-y-3">
          <textarea
            className={`${feldKlasse} min-h-[120px] font-mono text-xs`}
            placeholder="Buchungstag;Name;Verwendungszweck;Betrag&#10;05.01.2026;Max Mustermann;Miete Januar;800,00"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
          />
          {fehler && <p className="text-sm text-danger">{fehler}</p>}
          {meldung && <p className="text-sm text-success">{meldung}</p>}
          <Button groesse="sm" onClick={vorschau} disabled={laden || (!DEMO && !csv.trim())}>
            {laden ? 'Wird gelesen …' : 'Vorschau'}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {zeilen.length === 0 ? (
            <p className="text-sm text-fg-muted">Keine Zahlungseingänge im Auszug erkannt.</p>
          ) : (
            <div className="space-y-2">
              {zeilen.map((z, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-2 p-2">
                  <input type="checkbox" checked={z.uebernehmen} onChange={(e) => setZeilen((xs) => (xs ?? []).map((x, j) => (j === i ? { ...x, uebernehmen: e.target.checked } : x)))} />
                  <span className="text-xs text-fg-subtle">{z.datum ? formatDatum(z.datum) : '—'}</span>
                  <span className="tabellenziffern text-sm font-semibold text-fg">{z.betragCent != null ? formatEuro(z.betragCent) : '—'}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-fg-muted">{[z.name, z.zweck].filter(Boolean).join(' · ')}</span>
                  <select
                    className={`${feldKlasse} max-w-[12rem] text-xs`}
                    value={z.mvId}
                    onChange={(e) => setZeilen((xs) => (xs ?? []).map((x, j) => (j === i ? { ...x, mvId: e.target.value } : x)))}
                  >
                    <option value="">— nicht zuordnen —</option>
                    {leases.map((l) => (
                      <option key={l.id} value={l.id}>{l.mieter} · {l.einheit}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
          {fehler && <p className="text-sm text-danger">{fehler}</p>}
          <div className="flex gap-2">
            <Button groesse="sm" onClick={buchen} disabled={laden}>
              {laden ? '…' : `${zeilen.filter((z) => z.uebernehmen && z.mvId).length} Zahlung(en) buchen`}
            </Button>
            <Button variante="dezent" groesse="sm" onClick={() => setZeilen(null)}>Zurück</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

